import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

const BusinessPrivacy = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const [businessPolicy, setBusinessPolicy] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBusinessPolicy = async () => {
      if (!businessId) {
        setError('Business ID is required');
        setLoading(false);
        return;
      }

      try {
        // Fetch business info
        const { data: businessData, error: businessError } = await supabase
          .from('businesses')
          .select('name')
          .eq('id', businessId)
          .single();

        if (businessError) throw businessError;

        if (businessData) {
          setBusinessName(businessData.name);
        }

        // Fetch business policy
        const { data: policyData, error: policyError } = await supabase
          .from('business_policies')
          .select('custom_policy')
          .eq('business_id', businessId)
          .maybeSingle();

        if (policyError && policyError.code !== 'PGRST116') {
          throw policyError;
        }

        if (policyData) {
          setBusinessPolicy(policyData.custom_policy);
        }
      } catch (err) {
        console.error('Error fetching business policy:', err);
        setError('Failed to load business policy');
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessPolicy();
  }, [businessId]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow bg-background flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
              <p className="text-muted-foreground">{error}</p>
              <Link to="/privacy" className="text-primary hover:underline mt-4 inline-block">
                View our general privacy policy instead
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl font-bold mb-8 text-primary">Ochrana osobných údajov</h1>

          {businessName && (
            <div className="mb-8 p-4 bg-primary/10 rounded-lg border-l-4 border-primary">
              <p className="font-semibold">
                Táto stránka obsahuje zásady ochrany osobných údajov pre <span className="text-primary">{businessName}</span>
              </p>
            </div>
          )}

          <div className="prose prose-slate max-w-none space-y-6 text-foreground">
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-primary">Úvod</h2>
              <p>
                Táto Ochrana osobných údajov platí pre dve skupiny používateľov:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li><strong>Firemní zákazníci</strong> - podnikatelia, ktorí používajú našu SaaS platformu TagInn na riadenie svojho podnikania</li>
                <li><strong>Koncový používatelia</strong> - zákazníci našich firemných zákazníkov (napr. ľudia, ktorí získavajú vernostné karty, podávajú recenzie, prihlášky na pracovné pozície atď.)</li>
              </ul>
              <p className="mt-4">
                Prevádzkovateľom služby <strong>TagInn</strong> (ďalej len "Správca") je subjekt uvedený v sekcii Kontakt. Spracovanie osobných údajov prebieha v zmysle zákona č. 18/2018 Z. z. o ochrane osobných údajov a Nariadenia Európskeho parlamentu a Rady (EÚ) 2016/679 (GDPR).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-primary">1. Osobné údaje firemných zákazníkov</h2>
              <h3 className="text-xl font-semibold mt-4 mb-3">Aké údaje zbierame:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Meno a priezvisko</li>
                <li>Názov spoločnosti (ak ide o podnikateľa)</li>
                <li>Adresa</li>
                <li>E-mailová adresa</li>
                <li>Telefonické číslo</li>
                <li>IČO a DIČ (v prípade firemných zákazníkov)</li>
                <li>Údaje o používaní služby (analytické dáta)</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">Účel spracovania:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Poskytovanie SaaS služieb pre správu podnikania</li>
                <li>Evidencia a správa zákazníckych účtov</li>
                <li>Komunikácia a technická podpora</li>
                <li>Vystavovanie faktúr a vedenie účtovníctva</li>
                <li>Zasielanie marketingových informácií (len so súhlasom)</li>
                <li>Zlepšovanie kvality poskytovaných služieb</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">Doba uchovávania:</h3>
              <p>Údaje budú spracované po dobu trvania zmluvného vzťahu a následne po dobu 10 rokov v súlade s požiadavkami zákona o účtovníctve a daňových predpisov.</p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-primary">2. Osobné údaje koncových používateľov</h2>
              <p className="mb-4">
                Keď používate funkcie poskytované našimi firemných zákazníkmi (napríklad vernostné programy, podávanie recenzií, prihlášky na pracovné pozície), môžeme zbierať nasledujúce údaje:
              </p>

              <h3 className="text-xl font-semibold mt-4 mb-3">Vernostné programy a digitálne karty:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>E-mailová adresa (povinné)</li>
                <li>Meno a priezvisko (voliteľné)</li>
                <li>Telefónne číslo (voliteľné)</li>
                <li>Dátum narodenia (voliteľné)</li>
                <li>História transakcie a získané body</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">Recenzie a hodnotenia:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Meno (voliteľné)</li>
                <li>E-mailová adresa (voliteľné, na účely odmien)</li>
                <li>Hodnotenie a obsah recenzie</li>
                <li>Odpovede na vlastné otázky</li>
                <li>ID relácie (na účely analytiky)</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">Pracovné prihlášky:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Meno a priezvisko</li>
                <li>E-mailová adresa a telefónne číslo</li>
                <li>Adresa bydliska</li>
                <li>Dátum narodenia</li>
                <li>Životopis a motivačný list (súbory)</li>
                <li>Pracovná história a vzdelanie</li>
                <li>Referencie (mená, telefóny, e-maily)</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">Registrácie na podujatia:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Meno a priezvisko</li>
                <li>E-mailová adresa a telefónne číslo</li>
                <li>Stravovacie preferencie</li>
                <li>Núdzový kontakt</li>
                <li>Poznámky</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">Kupóny a zľavové akcie:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Meno a e-mailová adresa</li>
                <li>História uplatnenia kupónov</li>
                <li>ID digitálnej peňaženky (Google Wallet, Apple Wallet)</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">Kontaktné formuláre:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Meno</li>
                <li>E-mailová adresa</li>
                <li>Telefónne číslo (voliteľné)</li>
                <li>Obsah správy</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">Účel spracovania koncových údajov:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Poskytovanie vernostných programov a odmien</li>
                <li>Zasielanie digitálnych kariet do peňaženky</li>
                <li>Spracovanie recenzií a hodnotení</li>
                <li>Spracovanie pracovných prihlášok a náborový proces</li>
                <li>Organizácia podujatí a komunikácia s účastníkmi</li>
                <li>Spracovanie kupónov a zľavových akcií</li>
                <li>Odpovede na kontaktné formuláre</li>
                <li>Analytika a zlepšovanie služieb</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">Doba uchovávania koncových údajov:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Vernostné programy:</strong> Po dobu aktívneho členstva + 2 roky po poslednej aktivite</li>
                <li><strong>Recenzie:</strong> Pokiaľ nie sú vymazané na žiadosť alebo podnikateľom, po dobu prevádzky služby</li>
                <li><strong>Pracovné prihlášky:</strong> 2 roky od podania prihlášky (ak neboli vybrané), alebo počas zamestnania + 10 rokov (ak boli vybraté)</li>
                <li><strong>Registrácie na podujatia:</strong> 1 rok po ukončení podujatia</li>
                <li><strong>Kupóny:</strong> 3 roky od uplatnenia</li>
                <li><strong>Kontaktné formuláre:</strong> 2 roky od odoslania</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">Kto má prístup k vašim údajom:</h3>
              <p className="mb-4">
                Vaše osobné údaje môžu byť viditeľné nasledujúcim osobám, vždy iba v rozsahu nevyhnutnom na poskytovanie služby:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Majiteľ podniku</strong> - Má prístup k vašim úplným údajom (meno, email, telefón) na účely správy vernostného programu, spracovania prihlášok, organizácie podujatí a komunikácie s vami</li>
                <li><strong>Zamestnanci podniku</strong> - Pri overovaní vernostnej karty môžu vidieť vaše meno, aby vás mohli identifikovať a pripísať body. Nevidia váš email ani telefón.</li>
                <li><strong>Správcovia systému TagInn</strong> - Máme technický prístup k údajom výhradne na účely technickej podpory a riešenia problémov, vždy len so súhlasom podnikateľa</li>
              </ul>

              <p className="mt-6 bg-muted p-4 rounded-lg">
                <strong>Dôležité:</strong> Vaše údaje sú spracovávané v mene podnikateľa, ktorého služby používate.
                Ak máte otázky alebo chcete využiť svoje práva týkajúce sa údajov, môžete sa obrátiť priamo na tohoto
                podnikateľa alebo na nás pomocou kontaktných údajov uvedených nižšie.
              </p>
            </section>

            {/* Rest of the privacy policy sections - abbreviated for brevity */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-primary">3. Spracovanie osobných údajov</h2>
              <p>
                Súhlasom so spracovaním osobných údajov udeľuje dotknutá osoba svoj výslovný súhlas
                s vyššie uvedeným spracovaním. Súhlas možno vziať kedykoľvek späť, a to:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>Zaslaním emailu na adresu: <a href="mailto:info@taginn.app" className="text-primary hover:underline">info@taginn.app</a></li>
                <li>Telefonicky na čísle: +421 902 691 939</li>
              </ul>

              <p className="mt-6">
                Spracovanie osobných údajov je vykonávané Správcom. Osobné údaje môžu spracovávať
                aj títo spracovatelia:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>Poskytovateľ webhostingu a cloudových služieb (Supabase Inc.)</li>
                <li>Poskytovateľ platobných služieb (Stripe) - pripravované na budúce použitie</li>
                <li>Poskytovateľ emailových služieb (Resend)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-primary">4. Práva dotknutej osoby</h2>
              <p>
                Podľa zákona o ochrane osobných údajov a GDPR má každá osoba (firemní zákazníci aj koncoví používatelia) právo:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li><strong>Vziať súhlas kedykoľvek späť</strong> - bez toho, aby to malo vplyv na zákonnosť spracovania založeného na súhlase pred jeho odvolaním</li>
                <li><strong>Vyžiadať od nás informáciu</strong>, aké vaše osobné údaje spracovávame</li>
                <li><strong>Vyžiadať od nás vysvetlenie</strong> ohľadne spracovania osobných údajov</li>
                <li><strong>Vyžiadať si u nás výpis</strong> týchto údajov a tieto nechať aktualizovať alebo opraviť</li>
                <li><strong>Požadovať od nás vymazanie</strong> týchto osobných údajov (právo "byť zabudnutý")</li>
                <li><strong>Požadovať obmedzenie spracovania</strong> vašich osobných údajov</li>
                <li><strong>Namietať voči spracovaniu</strong> osobných údajov</li>
                <li><strong>Požadovať prenos údajov</strong> inému správcovi</li>
                <li><strong>Podať sťažnosť</strong> na Úrad na ochranu osobných údajov Slovenskej republiky</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-primary">Kontakt</h2>
              <p>
                V prípade akýchkoľvek otázok týkajúcich sa ochrany osobných údajov nás môžete
                kontaktovať:
              </p>
              <div className="mt-4 space-y-2">
                <p><strong>Správca osobných údajov:</strong></p>
                <p>TagInn</p>
                <p>Slovenská republika</p>
                <p>Email: <a href="mailto:info@taginn.app" className="text-primary hover:underline">info@taginn.app</a></p>
                <p>Telefón: +421 902 691 939</p>
              </div>
            </section>

            {businessPolicy && (
              <section className="border-t-4 border-primary pt-8 mt-12">
                <h2 className="text-3xl font-bold mb-6 text-primary">
                  Doplnkové zásady ochrany osobných údajov od {businessName || 'podniku'}
                </h2>
                <div className="prose prose-slate max-w-none whitespace-pre-wrap">
                  {businessPolicy}
                </div>
              </section>
            )}

            <p className="text-sm text-muted-foreground mt-8">
              Posledná aktualizácia: {new Date().toLocaleDateString('sk-SK')}
            </p>
          </div>

          <div className="mt-8">
            <Link to="/" className="text-primary hover:underline">
              ← Späť na hlavnú stránku
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BusinessPrivacy;
